import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, websites } from '@/mocks/navigation';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'shopping');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredWebsites = useMemo(
    () => websites.filter((w) => w.category === activeSlug),
    [activeSlug]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '分类导航', to: '/category/shopping' },
              { label: currentCategory?.name || '' },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-500">
              <i className={`${currentCategory?.icon || 'ri-global-line'} text-2xl text-background-50`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {currentCategory?.name}
            </h1>
            <p className="text-sm text-foreground-600 max-w-md mx-auto">
              {currentCategory?.description}
            </p>
            <p className="text-xs text-foreground-400 mt-2">
              共收录 <strong className="text-primary-600">{filteredWebsites.length}</strong> 个正品网站
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6 overflow-x-auto">
            <div className="flex items-center gap-1 py-2 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Website Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {filteredWebsites.map((site) => (
                <a
                  key={site.id}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-background-50 border border-background-200/70 hover:border-primary-300 rounded-xl p-4 md:p-5 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 group-hover:bg-primary-500 transition-colors">
                      <span className="text-sm font-bold text-primary-600 group-hover:text-background-50 transition-colors">
                        {site.name.charAt(0)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                        {site.name}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {site.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-background-100">
                    {site.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                    <div className="ml-auto flex items-center gap-1 text-xs text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>访问</span>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-up-line text-xs"></i>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.zhengpinlan.com" },
              { "@type": "ListItem", "position": 2, "name": "分类导航", "item": "https://www.zhengpinlan.com/category/shopping" },
              { "@type": "ListItem", "position": 3, "name": currentCategory?.name || "" },
            ]
          })
        }}
      />
    </>
  );
}